/**
 * `<page-explain>` — "why was this query denied?", routed at
 * `/instances/:id/explain`.
 *
 * It reproduces an access decision against a chosen bundle with a synthetic
 * subject by running `sluice policy explain` in a one-shot task on the
 * instance's pinned image (`SC-F704`, `SC-F700`, `SC-F332`). The statement is
 * parsed inside that container with the real pg_query backend, not a regex
 * fallback, so shape- and CEL-dependent policies explain exactly as the live
 * path would see them.
 *
 * Stories: `SC-S705`, and `SC-S707`'s last criterion — a failing test case opens
 * this page prefilled, so the failing assertion and the decision trace are one
 * step apart.
 *
 * **Most of this page's value is in what it refuses to imply.** `policy explain`
 * does not return the composed predicate text, mask arguments, the rewrite
 * effect, or any reason a policy *failed* to match; `effective.row_filters` comes
 * from a map iteration and is unordered; and it cannot model an **API-key
 * subject at all** — it describes a JWT-shaped identity only, so a policy that
 * behaves differently for an API-key consumer cannot be previewed here
 * (`SLUICE-U27`). Those sentences arrive on the response as `caveats` and are
 * rendered verbatim rather than paraphrased, because the API is where the
 * honesty originates and a client that re-words it gets it wrong.
 *
 * The three answers an author usually gets, per `concept.md` §6.5, each get their
 * own explanation rather than one shared "deny" badge, because they have three
 * different fixes: default-deny with nothing allowing the statement, an explicit
 * deny that short-circuited past every filter and mask, and a selector that looks
 * right and matches nothing — which explain can only show by omission.
 *
 * Nothing here executes SQL and no datasource is contacted.
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import type {
  BnBadgeVariant,
  BnCodeDiagnostic,
  BnColumn,
  BnKvRow,
  BnRow,
  BnSelectChangeDetail,
  BnSelectOption,
} from "../components/index.js";
import { decisionVariant } from "../components/index.js";

import {
  explain,
  parseExplainPrefill,
  type AppliedPolicy,
  type DecisionKind,
  type ExplainResult,
  type SyntheticSubject,
  type ValidationTarget,
} from "../api/cli.js";
import { ApiError, parseError, validationFields } from "../api/errors.js";
import { getInstance, type Instance } from "../api/instances.js";
import { cancelJob, isTerminal, jobFailure, watchJob, type Job } from "../api/jobs.js";
import { hrefFor, navigate } from "../nav.js";

const WAIT_SECONDS = 20;

/** The badge every surface built on explain or test carries. It is not a
 * decoration: the API-key path is the one most Cloud consumers actually have,
 * and it is the one the preview cannot model. */
const JWT_ONLY_BADGE = "JWT subject semantics only";

const TARGET_OPTIONS: BnSelectOption[] = [
  {
    value: "draft",
    label: "Draft",
    description: "What would happen if I published this — the working set.",
  },
  {
    value: "live",
    label: "Live generation",
    description: "The bundle the instance is serving now.",
  },
  {
    value: "generation",
    label: "A generation number",
    description:
      "Evaluate against the bundle that was live at the time, which is how an old denial is reproduced.",
  },
];

const MODE_OPTIONS: BnSelectOption[] = [
  {
    value: "sql",
    label: "A SQL statement",
    description: "Parsed inside the container with the real parser, so shape-dependent policies apply.",
  },
  {
    value: "table",
    label: "A table",
    description: "A three-part catalog.schema.table name, when the question is about a table rather than a query.",
  },
];

/** What each honest reading of the decision means, and what to do about it.
 * `SC-S705`'s last criterion is that an abstained denial is never labelled "a
 * policy denied this", because the two have different fixes. */
const DECISIONS: Record<
  DecisionKind,
  { label: string; variant: BnBadgeVariant; headline: string; meaning: string }
> = {
  allow: {
    label: "Allow",
    variant: "allow",
    headline: "A policy allowed this.",
    meaning:
      "The row filters and column masks below are what would be applied. Explain does not return the composed predicate or the mask arguments, so this is which filters and masks apply — not what they would do to the data.",
  },
  no_policy_allowed: {
    label: "Deny",
    variant: "deny",
    headline: "No policy allowed this.",
    meaning:
      "This is the engine's default-deny: nothing matched that permits the statement, and no policy objected either. The fix is an allow that covers this subject and this resource — not a change to an existing policy, because no existing policy is involved.",
  },
  denied_by_policy: {
    label: "Deny",
    variant: "deny",
    headline: "A policy denied this.",
    meaning:
      "One policy matched and refused; it is named below with its reason. A deny short-circuits, so every row filter, column mask and approval requirement was skipped — their absence below is not evidence that none of them apply.",
  },
  rejected: {
    label: "Reject",
    variant: "deny",
    headline: "A rejection rule matched.",
    meaning:
      "A QueryRejectPolicy refused the statement before it could be evaluated further. The rejecting rule and its reason are below.",
  },
  unknown: {
    label: "Unknown",
    variant: "warning",
    headline: "This build does not recognise the outcome the engine reported.",
    meaning:
      "The value is shown verbatim below rather than coerced into an allow or a deny. A newer Sluice than this bundle knows about is the likely explanation.",
  },
};

const POLICY_COLUMNS: BnColumn[] = [
  { key: "kind", label: "Kind" },
  { key: "name", label: "Name" },
  { key: "priority", label: "Priority", width: "1px", align: "right" },
];

const REJECTED_COLUMNS: BnColumn[] = [
  { key: "kind", label: "Kind" },
  { key: "name", label: "Name" },
  { key: "reason", label: "Reason it refused" },
];

const MASK_COLUMNS: BnColumn[] = [
  { key: "column", label: "Column", mono: true },
  { key: "mask_type", label: "Mask" },
  { key: "policy", label: "From policy" },
];

/** One editable claim row. Kept as a list rather than a map so a half-typed key
 * does not collide with another and silently drop a row. */
interface ClaimRow {
  key: string;
  value: string;
}

/**
 * `<page-explain>` reproduces one access decision with a synthetic subject.
 *
 * Implements `SC-F704` (proxied `policy explain`), `SC-F700` (the one-shot task
 * on the pinned image) and `SC-F332` (the job), and is the drill-down target
 * `SC-F809` and `SC-F707`'s failing cases open. It renders the API's `caveats`
 * verbatim, including `SLUICE-U27` — explain describes a JWT-shaped identity
 * only, so an API-key consumer cannot be previewed at all.
 *
 * Stories: `SC-S705`, `SC-S707` (its last criterion), `SC-S809`.
 *
 *   <page-explain .instanceId=${id}></page-explain>
 */
@customElement("page-explain")
export class PageExplain extends LitElement {
  @property() instanceId = "";

  @state() private instance: Instance | null = null;
  @state() private loading = true;
  @state() private loadError: ApiError | null = null;

  @state() private targetKind: "draft" | "live" | "generation" = "draft";
  @state() private generation = "";
  @state() private mode: "sql" | "table" = "sql";
  @state() private sql = "";
  @state() private table = "";
  @state() private user = "";
  @state() private issuer = "";
  @state() private email = "";
  @state() private groups: string[] = [];
  @state() private claims: ClaimRow[] = [];

  /** Field-level refusals, keyed by the API's own field paths so a `422` lands
   * on the control that produced it. */
  @state() private fieldErrors: Record<string, string> = {};

  @state() private job: Job<ExplainResult> | null = null;
  @state() private result: ExplainResult | null = null;
  @state() private runError: ApiError | null = null;
  @state() private running = false;

  /** The statement the answer on screen was produced from. An answer about text
   * that has since been edited is a wrong answer presented as a right one, so
   * the panel says the statement moved rather than quietly describing the old
   * one. */
  @state() private ranSql = "";

  private abort?: AbortController;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    header {
      margin-bottom: var(--bino-space-lg);
    }
    .head-line {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    h2 {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
    }
    .lede {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      max-width: 80ch;
    }
    .stack {
      display: grid;
      gap: var(--bino-space-lg);
    }
    .grid {
      display: grid;
      gap: var(--bino-space-md);
      grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
    }
    .row {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-end;
      gap: var(--bino-space-md);
    }
    .row bn-field,
    .grid bn-field {
      margin-bottom: 0;
    }
    .narrow {
      width: 16rem;
    }
    .repeater {
      display: grid;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-md);
    }
    .repeater-row {
      display: flex;
      align-items: flex-start;
      gap: var(--bino-space-sm);
    }
    .repeater-row bn-input {
      flex: 1 1 8rem;
      min-width: 0;
    }
    fieldset {
      border: 1px solid var(--bino-border-light);
      border-radius: var(--bino-radius);
      padding: var(--bino-space-md);
      margin: 0 0 var(--bino-space-md);
    }
    legend {
      padding: 0 var(--bino-space-xs);
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
      color: var(--bino-text-muted);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    p {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      max-width: 80ch;
    }
    p:last-child {
      margin-bottom: 0;
    }
    ul {
      margin: 0 0 var(--bino-space-sm);
      padding-left: var(--bino-space-lg);
      max-width: 80ch;
    }
    li {
      margin-bottom: var(--bino-space-xs);
      font-size: var(--bino-font-size-md);
    }
    .muted {
      color: var(--bino-text-secondary);
      font-size: var(--bino-font-size-sm);
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    .actions {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
    .verdict {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
      margin-bottom: var(--bino-space-sm);
    }
    .verdict-line {
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
    }
    .chips {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-xs);
      margin-bottom: var(--bino-space-sm);
    }
    .chip {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      padding: 0.125rem var(--bino-space-sm);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-surface-inset);
      color: var(--bino-text);
    }
    .section {
      margin-top: var(--bino-space-lg);
    }
    .section:first-of-type {
      margin-top: 0;
    }
    a.inline {
      color: var(--bino-primary);
      font-weight: 600;
      text-decoration: none;
    }
    a.inline:hover {
      text-decoration: underline;
    }
    a.inline:focus-visible {
      outline: none;
      border-radius: var(--bino-radius-sm);
      box-shadow: var(--bino-focus-ring-shadow);
    }
    .error-message {
      margin: 0 0 var(--bino-space-md);
      max-width: 70ch;
    }
    .error-actions {
      display: flex;
      gap: var(--bino-space-sm);
      margin-top: var(--bino-space-md);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    this.applyPrefill();
    void this.load();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.abort?.abort();
  }

  override updated(changed: Map<string, unknown>) {
    if (changed.has("instanceId") && changed.get("instanceId") !== undefined) void this.load();
  }

  /**
   * A prefilled explain is how a failing test case, a playground statement and
   * an audit record all arrive here. The query-string spelling lives in
   * `api/cli.ts` so the three senders and this reader cannot disagree.
   */
  private applyPrefill() {
    const pre = parseExplainPrefill(location.search);
    if (pre.target === "live" || pre.target === "draft") this.targetKind = pre.target;
    else if (typeof pre.target === "number") {
      this.targetKind = "generation";
      this.generation = String(pre.target);
    }
    if (pre.subject.user) this.user = pre.subject.user;
    if (pre.subject.issuer) this.issuer = pre.subject.issuer;
    if (pre.subject.email) this.email = pre.subject.email;
    if (pre.subject.groups) this.groups = [...pre.subject.groups];
    if (pre.subject.claims) {
      this.claims = Object.entries(pre.subject.claims).map(([key, value]) => ({ key, value }));
    }
    if (pre.table) {
      this.mode = "table";
      this.table = pre.table;
    }
    if (pre.sql) {
      this.mode = "sql";
      this.sql = pre.sql;
    }
  }

  private async load() {
    if (!this.instanceId) return;
    this.loading = true;
    try {
      this.instance = await getInstance(this.instanceId);
      this.loadError = null;
    } catch (err) {
      this.loadError = parseError(err);
    } finally {
      this.loading = false;
    }
  }

  /* ── the subject form ──────────────────────────────────────────────────── */

  private get target(): ValidationTarget {
    if (this.targetKind === "generation") return Number(this.generation);
    return this.targetKind;
  }

  /**
   * The client-side rules are the API's own, restated so a refusal costs no
   * container start and lands on the field that caused it. Every one of them
   * mirrors a `pflag` behaviour or a check inside the CLI: `--groups` and
   * `--claims` are CSV-split, and `--claims` takes `key=value` text and parses
   * nothing richer.
   */
  private validateForm(): Record<string, string> {
    const errs: Record<string, string> = {};
    if (this.user.trim() === "") {
      errs["subject.user"] = "Required: explain evaluates one named subject.";
    }
    this.groups.forEach((g, i) => {
      if (g.trim() === "") errs[`subject.groups.${i}`] = "Remove the row or give it a name.";
      else if (g.includes(",")) {
        errs[`subject.groups.${i}`] =
          "A group may not contain a comma: the CLI splits --groups on commas, so this would silently become two groups.";
      }
    });
    const seen = new Set<string>();
    this.claims.forEach((c, i) => {
      const key = c.key.trim();
      if (key === "" && c.value.trim() === "") return;
      if (key === "") errs[`subject.claims.${i}`] = "A claim needs a name.";
      else if (/[,=]/.test(key)) {
        errs[`subject.claims.${i}`] = "A claim name may not contain a comma or an equals sign.";
      } else if (seen.has(key)) {
        errs[`subject.claims.${i}`] = `${key} is already set above.`;
      }
      if (c.value.includes(",")) {
        errs[`subject.claims.${i}`] =
          "A claim value may not contain a comma: the CLI splits --claims on commas.";
      }
      seen.add(key);
    });
    if (this.mode === "sql" && this.sql.trim() === "") {
      errs["sql"] = "Paste the statement to explain.";
    }
    if (this.mode === "table") {
      const parts = this.table.split(".");
      if (this.table.trim() === "") errs["table"] = "Name the table to explain.";
      else if (parts.length !== 3 || parts.some((p) => p.trim() === "")) {
        errs["table"] = "Must be catalog.schema.table, with three non-empty segments.";
      }
    }
    if (this.targetKind === "generation" && !/^[1-9][0-9]*$/.test(this.generation.trim())) {
      errs["target"] = "A positive generation number.";
    }
    return errs;
  }

  private get subject(): SyntheticSubject {
    const claims: Record<string, string> = {};
    for (const c of this.claims) {
      const key = c.key.trim();
      if (key !== "") claims[key] = c.value;
    }
    const out: SyntheticSubject = { user: this.user.trim() };
    if (this.issuer.trim()) out.issuer = this.issuer.trim();
    if (this.email.trim()) out.email = this.email.trim();
    const groups = this.groups.map((g) => g.trim()).filter((g) => g !== "");
    if (groups.length > 0) out.groups = groups;
    if (Object.keys(claims).length > 0) out.claims = claims;
    return out;
  }

  private async run() {
    const errs = this.validateForm();
    this.fieldErrors = errs;
    if (Object.keys(errs).length > 0) return;

    this.abort?.abort();
    this.abort = new AbortController();
    this.running = true;
    this.runError = null;
    this.result = null;
    this.job = null;
    this.ranSql = this.mode === "sql" ? this.sql : "";
    try {
      const started = await explain(
        this.instanceId,
        {
          target: this.target,
          subject: this.subject,
          sql: this.mode === "sql" ? this.sql : undefined,
          table: this.mode === "table" ? this.table.trim() : undefined,
        },
        { waitSeconds: WAIT_SECONDS },
      );
      this.job = started.job;
      const done = isTerminal(started.job.status)
        ? started.job
        : await watchJob<ExplainResult>(started.job.id, {
            waitSeconds: WAIT_SECONDS,
            onUpdate: (j) => (this.job = j),
            signal: this.abort.signal,
          });
      this.job = done;
      const failure = jobFailure(done);
      if (failure) {
        this.runError = failure;
        return;
      }
      this.result = done.result;
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      const parsed = parseError(err);
      this.runError = parsed;
      const fields = validationFields(parsed);
      if (fields.length > 0) {
        this.fieldErrors = Object.fromEntries(fields.map((f) => [f.path, f.reason]));
      }
    } finally {
      this.running = false;
    }
  }

  private async cancel() {
    const id = this.job?.id;
    if (!id) return;
    try {
      await cancelJob(id);
    } catch (err) {
      this.runError = parseError(err);
    }
  }

  /* ── rendering ─────────────────────────────────────────────────────────── */

  override render() {
    if (this.loading && !this.instance) {
      return html`<bn-spinner block size="28" label="Loading this instance"></bn-spinner>`;
    }
    if (this.loadError) return this.renderLoadError(this.loadError);
    return html`
      <header>
        <div class="head-line">
          <h1>Explain a decision</h1>
          <bn-badge variant="warning">${JWT_ONLY_BADGE}</bn-badge>
        </div>
        <p class="lede">
          Reproduce an access decision with a subject you describe. The statement is parsed by the
          real parser inside this instance's pinned image, so the answer is the one the live path
          would give — for a JWT-shaped subject. Nothing is executed and no rows exist.
        </p>
      </header>
      <div class="stack">
        ${this.renderForm()} ${this.renderRunState()} ${this.renderOutcome()}
        ${this.renderLimitations()}
      </div>
    `;
  }

  private renderForm() {
    return html`<bn-card heading="What to explain">
      <div class="row">
        <bn-field class="narrow" label="Evaluate against">
          <bn-select
            .options=${TARGET_OPTIONS}
            .value=${this.targetKind}
            @change=${(e: CustomEvent<BnSelectChangeDetail>) =>
              (this.targetKind = e.detail.value as "draft" | "live" | "generation")}
          ></bn-select>
        </bn-field>
        ${this.targetKind === "generation"
          ? html`<bn-field
              label="Generation number"
              required
              .error=${this.fieldErrors["target"] ?? ""}
            >
              <bn-input
                mono
                inputmode="numeric"
                .value=${this.generation}
                @input=${(e: CustomEvent<{ value: string }>) => (this.generation = e.detail.value)}
              ></bn-input>
            </bn-field>`
          : nothing}
        <bn-field class="narrow" label="Ask about">
          <bn-select
            .options=${MODE_OPTIONS}
            .value=${this.mode}
            @change=${(e: CustomEvent<BnSelectChangeDetail>) =>
              (this.mode = e.detail.value as "sql" | "table")}
          ></bn-select>
        </bn-field>
      </div>

      ${this.mode === "sql" ? this.renderSqlField() : this.renderTableField()}
      ${this.renderSubjectFieldset()}

      <div class="actions">
        <bn-button variant="primary" ?loading=${this.running} @click=${() => void this.run()}>
          ${this.running ? "Explaining…" : "Explain"}
        </bn-button>
        ${this.running && this.job
          ? html`<bn-button @click=${() => void this.cancel()}>Cancel</bn-button>`
          : nothing}
        <span class="muted">
          A decision, never rows. Explaining as a synthetic subject reads policy you can already
          read; executing as a subject is a different capability entirely.
        </span>
      </div>
    </bn-card>`;
  }

  /**
   * The SQL surface is `bn-code`, never a text area: it is the one place a parse
   * error can be drawn on the statement itself. The parser reports no position —
   * `policy explain` prints `parse --sql: <message>` and nothing more — so the
   * diagnostic spans the whole statement and the copy says why, rather than
   * pointing at a character the parser never named.
   */
  private renderSqlField() {
    // The diagnostic is drawn only while the text is still the text that was
    // explained: character offsets into an edited document point at the wrong
    // thing.
    const parserMessage = this.sql === this.ranSql ? (this.result?.parse_error ?? "") : "";
    const diagnostics: BnCodeDiagnostic[] = parserMessage
      ? [{ from: 0, to: this.sql.length, severity: "error", message: parserMessage }]
      : [];
    return html`<bn-field
      label="SQL statement"
      required
      .error=${this.fieldErrors["sql"] ?? ""}
      hint="Parsed with Sluice's own parser inside the container. Shape-dependent policies therefore see the real query shape."
    >
      <bn-code
        language="sql"
        label="SQL statement to explain"
        filename="explain.sql"
        .readonly=${false}
        .value=${this.sql}
        .diagnostics=${diagnostics}
        @bn-code-change=${(e: CustomEvent<{ value: string }>) => (this.sql = e.detail.value)}
      ></bn-code>
    </bn-field>`;
  }

  private renderTableField() {
    return html`<bn-field
      label="Table"
      required
      .error=${this.fieldErrors["table"] ?? ""}
      hint="Three parts: catalog.schema.table. The catalog is the datasource's name."
    >
      <bn-input
        mono
        placeholder="warehouse.public.customers"
        .value=${this.table}
        @input=${(e: CustomEvent<{ value: string }>) => (this.table = e.detail.value)}
      ></bn-input>
    </bn-field>`;
  }

  private renderSubjectFieldset() {
    return html`<fieldset>
      <legend>The subject</legend>
      <div class="grid">
        <bn-field
          label="Subject id"
          required
          .error=${this.fieldErrors["subject.user"] ?? ""}
          hint="What the binding resolves as the subject — usually the sub claim."
        >
          <bn-input
            .value=${this.user}
            @input=${(e: CustomEvent<{ value: string }>) => (this.user = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-field label="Issuer" hint="The synthetic iss claim.">
          <bn-input
            .value=${this.issuer}
            @input=${(e: CustomEvent<{ value: string }>) => (this.issuer = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-field label="Email">
          <bn-input
            type="email"
            .value=${this.email}
            @input=${(e: CustomEvent<{ value: string }>) => (this.email = e.detail.value)}
          ></bn-input>
        </bn-field>
      </div>

      <bn-field
        label="Groups"
        hint="One per row. A group may not contain a comma — the CLI splits the flag on commas, so a comma would silently become two groups."
      >
        <div class="repeater" role="group">
          ${this.groups.map(
            (g, i) => html`<div class="repeater-row">
              <bn-input
                aria-label=${`Group ${i + 1}`}
                .value=${g}
                .diagnostic=${this.fieldErrors[`subject.groups.${i}`] ?? ""}
                @input=${(e: CustomEvent<{ value: string }>) => {
                  const next = [...this.groups];
                  next[i] = e.detail.value;
                  this.groups = next;
                }}
              ></bn-input>
              <bn-button
                size="sm"
                variant="ghost"
                @click=${() => (this.groups = this.groups.filter((_, j) => j !== i))}
              >
                Remove
              </bn-button>
            </div>`,
          )}
          <div>
            <bn-button size="sm" @click=${() => (this.groups = [...this.groups, ""])}>
              Add a group
            </bn-button>
          </div>
        </div>
      </bn-field>

      <bn-field
        label="Claims"
        hint="Values are strings and nothing richer: the CLI delivers claims as key=value text, so a number could not be delivered as a number. Commas are refused for the same reason as in groups."
      >
        <div class="repeater" role="group">
          ${this.claims.map(
            (c, i) => html`<div class="repeater-row">
              <bn-input
                aria-label=${`Claim ${i + 1} name`}
                mono
                .value=${c.key}
                .diagnostic=${this.fieldErrors[`subject.claims.${i}`] ?? ""}
                @input=${(e: CustomEvent<{ value: string }>) => this.setClaim(i, "key", e.detail.value)}
              ></bn-input>
              <bn-input
                aria-label=${`Claim ${i + 1} value`}
                .value=${c.value}
                @input=${(e: CustomEvent<{ value: string }>) =>
                  this.setClaim(i, "value", e.detail.value)}
              ></bn-input>
              <bn-button
                size="sm"
                variant="ghost"
                @click=${() => (this.claims = this.claims.filter((_, j) => j !== i))}
              >
                Remove
              </bn-button>
            </div>`,
          )}
          <div>
            <bn-button
              size="sm"
              @click=${() => (this.claims = [...this.claims, { key: "", value: "" }])}
            >
              Add a claim
            </bn-button>
          </div>
        </div>
      </bn-field>

      <p class="muted">
        On the JWT path a template reaches a claim as
        <code>{{ subject.claims.&lt;path&gt; }}</code>. The <code>tenantId</code> and
        <code>allowedRegions</code> shorthands resolve only in API-key mode, which explain cannot
        simulate at all — so a policy that depends on them cannot be previewed here.
      </p>
    </fieldset>`;
  }

  private setClaim(index: number, field: "key" | "value", value: string) {
    const next = this.claims.map((c, i) => (i === index ? { ...c, [field]: value } : c));
    this.claims = next;
  }

  private renderRunState() {
    const job = this.job;
    if (!job || !this.running) return nothing;
    return html`<bn-panel eyebrow="Run in progress">
      <div class="actions">
        <bn-spinner
          label=${job.status === "queued"
            ? "Queued — waiting for a slot on the cluster"
            : "Running on the pinned image"}
        ></bn-spinner>
        <a class="inline" href=${hrefFor("jobs", job.id)}>Job ${job.id}</a>
      </div>
    </bn-panel>`;
  }

  private renderOutcome() {
    if (this.runError) return this.renderRunError(this.runError);
    const r = this.result;
    if (!r) return this.running ? nothing : this.renderNeverRun();
    if (r.parse_error) return this.renderParseError(r);
    if (!r.explain) return nothing;
    return this.renderDecision(r);
  }

  private renderNeverRun() {
    return html`<bn-card>
      <bn-empty-state
        icon="?"
        heading="Nothing explained yet"
        message="Describe a subject and a statement, and this reproduces the decision the engine would reach — which policies matched, which ran in shadow mode, which refused, and the row filters and column masks that would apply."
      >
        <bn-button slot="actions" variant="primary" @click=${() => void this.run()}>
          Explain
        </bn-button>
      </bn-empty-state>
    </bn-card>`;
  }

  /**
   * A parse error is the customer's SQL, not a policy result, and `SC-S714`'s
   * fourth criterion is explicit that it must not be counted as a denial. The
   * job succeeded; the statement did not parse.
   */
  private renderParseError(r: ExplainResult) {
    return html`<bn-card heading="That statement did not parse">
      <div role="alert">
        ${this.renderStaleNote()}
        <p>
          Sluice's parser refused the statement, so no policy was evaluated. This is a syntax error
          and <strong>not</strong> a denial — nothing about your policies is implicated.
        </p>
        <bn-kv
          .rows=${[
            { label: "Parser message", value: r.parse_error ?? "" },
            { label: "Duration", value: `${r.duration_ms} ms`, copyable: false },
          ] as BnKvRow[]}
        ></bn-kv>
        <p class="muted">
          The parser reports no position, so the whole statement is marked in the editor above rather
          than a character it never named.
        </p>
      </div>
    </bn-card>`;
  }

  private renderDecision(r: ExplainResult) {
    const e = r.explain;
    if (!e) return nothing;
    const facts = DECISIONS[e.decision_kind] ?? DECISIONS.unknown;
    return html`
      <bn-card heading="Decision">
        ${this.renderStaleNote()}
        <div class="verdict">
          <bn-badge .variant=${facts.variant}>${facts.label}</bn-badge>
          <span class="verdict-line">${facts.headline}</span>
          <bn-badge .variant=${decisionVariant(e.effective.decision)}
            >engine: ${e.effective.decision}</bn-badge
          >
        </div>
        <p>${facts.meaning}</p>
        ${e.decision_kind === "no_policy_allowed"
          ? html`<p class="muted">
              "Nothing allowed this" versus "a policy denied this" is read off an empty rejected
              list, because the engine's own abstained flag is not part of the explain payload. It is
              right for every case this build can produce, and it is still an inference.
            </p>`
          : nothing}
        <bn-kv .rows=${this.decisionRows(r)}></bn-kv>
      </bn-card>

      <bn-card heading="What would be applied">
        <div class="section">
          <h2>Row filters</h2>
          ${e.effective.row_filters.length === 0
            ? html`<p class="muted">None.</p>`
            : html`<div class="chips">
                  ${e.effective.row_filters.map((t) => html`<span class="chip">${t}</span>`)}
                </div>
                <p class="muted">
                  This is a <strong>set of tables that carry a filter</strong>, in no meaningful
                  order — it comes from a map iteration upstream. Explain returns no composed
                  predicate text, so it says which tables are filtered and never what the filter
                  says.
                </p>`}
        </div>
        <div class="section">
          <h2>Column masks</h2>
          ${e.effective.column_masks.length === 0
            ? html`<p class="muted">None.</p>`
            : html`<bn-table
                  caption="Column masks that would apply"
                  .columns=${MASK_COLUMNS}
                  .rows=${e.effective.column_masks as unknown as BnRow[]}
                ></bn-table>
                <p class="muted">
                  Mask arguments are not in the payload, so this is which mask would claim each
                  column — not what the value would look like afterwards. Masks never stack: one
                  policy wins per column.
                </p>`}
        </div>
      </bn-card>

      ${this.renderPolicySection(
        "Matched",
        "These selectors matched and affected the decision.",
        e.matched,
      )}
      ${this.renderPolicySection(
        "Shadow",
        "These matched while running in Audit or DryRun, so they reported and changed nothing. A shadow policy is not enforced.",
        e.shadow,
      )}
      ${e.rejected.length > 0
        ? html`<bn-card heading="Refused by">
            <bn-table
              caption="Policies that matched and refused"
              .columns=${REJECTED_COLUMNS}
              .rows=${e.rejected.map((p) => ({
                kind: p.kind,
                name: this.policyLink(p.kind, p.name, p.policy_id),
                reason: p.reason,
              })) as BnRow[]}
            ></bn-table>
          </bn-card>`
        : nothing}
      ${this.renderPolicySection(
        "Approval required",
        "These would hold the query for an approval before it ran.",
        e.approval_required,
      )}
      ${this.renderCaveats(r)}
    `;
  }

  /** Says so when the statement has been edited since this answer was produced.
   * An answer about text nobody can see any more is the one thing a debugger must
   * not present as current. */
  private renderStaleNote() {
    if (this.mode !== "sql" || this.sql === this.ranSql) return nothing;
    return html`<p class="muted" role="status">
      The statement above has been edited since this answer. Run it again to explain what is in the
      editor now.
    </p>`;
  }

  private decisionRows(r: ExplainResult): BnKvRow[] {
    const e = r.explain;
    const rows: BnKvRow[] = [
      { label: "Subject", value: e?.subject ?? "" },
      { label: "Resource", value: e?.resource ?? "" },
      { label: "Evaluated against", value: targetLabel(r.target), copyable: false },
    ];
    if (r.target.digest) rows.push({ label: "Bundle digest", value: r.target.digest });
    if (r.sluice_version) rows.push({ label: "Sluice version", value: r.sluice_version });
    rows.push({ label: "Duration", value: `${r.duration_ms} ms`, copyable: false });
    rows.push({
      label: "Rows returned",
      value: `${r.dry_run.rows_returned} — nothing was executed`,
      copyable: false,
    });
    if (this.job) rows.push({ label: "Job", value: this.job.id });
    if (r.run_id) rows.push({ label: "CLI run", value: r.run_id });
    return rows;
  }

  private renderPolicySection(heading: string, blurb: string, policies: AppliedPolicy[]) {
    if (policies.length === 0) return nothing;
    return html`<bn-card heading=${heading}>
      <p class="muted">${blurb}</p>
      <bn-table
        caption=${heading}
        .columns=${POLICY_COLUMNS}
        .rows=${policies.map((p) => ({
          kind: p.kind,
          name: this.policyLink(p.kind, p.name, p.policy_id),
          priority: p.priority,
        })) as BnRow[]}
      ></bn-table>
    </bn-card>`;
  }

  /** A policy the control plane could resolve opens its editor. One it could not
   * is shown as plain text: a guessed link would open the wrong object with
   * confidence, which is worse than no link. */
  private policyLink(kind: string, name: string, policyId: string | null): TemplateResult {
    if (!policyId) return html`<span>${name}</span>`;
    return html`<a class="inline" href=${hrefFor("instances", this.instanceId, "policies", kind, name)}
      >${name}</a
    >`;
  }

  /** The caveats, verbatim from the response. Every explain carries them,
   * including the `SLUICE-U27` one, and not only when the request looked
   * API-key-shaped — the API cannot tell. */
  private renderCaveats(r: ExplainResult) {
    if (r.caveats.length === 0) return nothing;
    return html`<bn-card heading="What this answer does not tell you">
      <ul>
        ${r.caveats.map((c) => html`<li>${c}</li>`)}
      </ul>
      <p class="muted">
        These come from the API on every explain. They are limits of the tool, not of this screen.
      </p>
    </bn-card>`;
  }

  /**
   * The standing explainer: the three answers an author usually gets, and the
   * one this tool can only show by omission. It is rendered whether or not a run
   * has happened, because the third case — a selector that looks right and
   * matches nothing — is invisible in a result and is the one that costs the most
   * time.
   */
  private renderLimitations() {
    return html`<bn-card heading="Reading an explain">
      <ul>
        <li>
          <strong>Nothing allowed it.</strong> Default-deny with no matching allow. No policy is
          named because none objected — the fix is an allow, not an edit.
        </li>
        <li>
          <strong>Something denied it.</strong> A policy matched and refused, and the deny
          short-circuited: every row filter, column mask and approval requirement was skipped, so
          this answer says nothing about them.
        </li>
        <li>
          <strong>A selector matches nothing.</strong> If a policy you expected is not listed above,
          explain cannot tell you why — it reports what matched and never why something did not. That
          is what the pattern preview in the
          <a class="inline" href=${hrefFor("instances", this.instanceId, "policies")}>policy editor</a>
          is for.
        </li>
      </ul>
      <p class="muted">
        And one limit worth repeating: the simulated subject is always JWT-shaped. An API-key subject
        carries only the literal claims the binding stamps, so a policy that behaves differently for
        an API-key consumer cannot be previewed here at all (<code>SLUICE-U27</code>). The only real
        coverage for that path is a live call with a real minted key.
      </p>
    </bn-card>`;
  }

  private renderRunError(err: ApiError) {
    const rows: BnKvRow[] = [{ label: "Error code", value: err.code }];
    if (this.job) rows.push({ label: "Job", value: this.job.id });
    if (err.requestId) rows.push({ label: "Request id", value: err.requestId });
    const upstream = err.details?.["upstream"];
    if (typeof upstream === "string") rows.push({ label: "Upstream ask", value: upstream });
    const reason = err.details?.["reason"];
    if (typeof reason === "string") rows.push({ label: "Reason", value: reason });
    return html`<bn-card heading="No explanation was produced">
      <div role="alert">
        <p class="error-message">${err.serverMessage || err.message}</p>
        <bn-kv .rows=${rows}></bn-kv>
        ${err.code === "policy_invalid"
          ? html`<p class="muted">
                This bundle does not compile, so there is nothing to explain against it. Validate it
                first — the findings there name what to fix.
              </p>
              <div class="error-actions">
                <bn-button
                  variant="primary"
                  @click=${() => navigate(hrefFor("instances", this.instanceId, "validation"))}
                >
                  Open validation
                </bn-button>
              </div>`
          : html`<div class="error-actions">
              <bn-button variant="primary" @click=${() => void this.run()}>Try again</bn-button>
            </div>`}
      </div>
    </bn-card>`;
  }

  private renderLoadError(err: ApiError) {
    return html`<bn-card heading="Could not load this instance">
      <div role="alert">
        <p class="error-message">${err.message}</p>
        <bn-kv
          .rows=${[
            { label: "Error code", value: err.code },
            ...(err.requestId ? [{ label: "Request id", value: err.requestId }] : []),
          ] as BnKvRow[]}
        ></bn-kv>
        <div class="error-actions">
          <bn-button variant="primary" @click=${() => void this.load()}>Try again</bn-button>
        </div>
      </div>
    </bn-card>`;
  }
}

function targetLabel(t: { kind: string; generation?: number }): string {
  if (t.kind === "generation") return `generation ${t.generation ?? "?"}`;
  if (t.kind === "live") return `live generation${t.generation ? ` (${t.generation})` : ""}`;
  return t.generation ? `draft (generation ${t.generation})` : "draft";
}

declare global {
  interface HTMLElementTagNameMap {
    "page-explain": PageExplain;
  }
}
